import { badgeView, type BadgeKey, type NavBadge as NavBadgeData } from '@aithora/shared';

/**
 * One menu status label. 'status' items (Guardian, Worklist, Atlas) draw a dot and
 * a word; the rest draw a count or short figure. The label is real text with its
 * server-written sentence as the accessible name.
 */
export function NavBadge({
  badgeKey,
  badge,
  variant,
  compact,
  building,
}: {
  badgeKey: BadgeKey;
  badge: NavBadgeData | null | undefined;
  variant: 'status' | 'count';
  compact?: boolean;
  /** System Pulse scoped to one building (Facility Admin): the design's smaller pill. */
  building?: boolean;
}) {
  const v = badgeView(badgeKey, badge);
  if (!v) return null;
  // Collapsed menu: a dot keeps a safety or attention label visible, with the same sentence.
  if (compact) {
    if (v.tone === 'green') return null;
    return (
      <span className={`nav-badge-dot tone-${v.tone}`} title={v.aria} data-testid={`badge-${badgeKey}`} data-tone={v.tone}>
        <span className="sr-only">{v.aria}</span>
      </span>
    );
  }
  return (
    <span
      className={`nav-badge nav-badge--${variant} tone-${v.tone}${badgeKey === 'pulse' ? ' nav-badge--pulse' : ''}${building ? ' nav-badge--building' : ''}`}
      title={v.aria}
      data-testid={`badge-${badgeKey}`}
      data-tone={v.tone}
    >
      {variant === 'status' && <span className="nav-badge__dot" aria-hidden="true" />}
      <span aria-hidden="true">{v.text}</span>
      <span className="sr-only">{`${v.aria}${v.modeled ? ' (modeled)' : ''}`}</span>
      {v.modeled && (
        <span className="nav-badge__modeled" aria-hidden="true" title="Partly modeled">
          ~
        </span>
      )}
    </span>
  );
}
